import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import apiClient from '../services/apiClient';
import AuthService from '../../modules/Admin/services/AuthService';
import { isSuperAdminRole } from '../../routes/roles';
import { fetchTenantIndustry } from '../services/TenantService';
import AgencyUserDrawer from '../../modules/SuperAdmin/components/Client/AgencyUserDrawer';
import PlanFeaturesList from './PlanFeaturesList';
import { buildPlanFeatureRows, countEnabledFeatures } from '../utils/planFeatureRows';

function readIndustryName() {
  try {
    const raw = sessionStorage.getItem('nf_tenant_industry_v2');
    if (!raw) return '';
    const parsed = JSON.parse(raw);
    return parsed?.categoryName ?? parsed?.CategoryName ?? '';
  } catch {
    return '';
  }
}

export default function SidebarPlanCard() {
  const roleId = AuthService.getLoggedInRoleId();
  const clientId = AuthService.getLoggedInClientId();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [industryName, setIndustryName] = useState(readIndustryName);

  const loadPlan = useCallback(async () => {
    if (!clientId || isSuperAdminRole(roleId)) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const token = AuthService.getToken();
      if (!token || AuthService.isTokenExpired(token)) {
        setPlan(null);
        return;
      }
      const [res] = await Promise.all([
        apiClient.get('/Subscription/GetMySubscriptionStatus', {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetchTenantIndustry(clientId).catch(() => null),
      ]);
      setIndustryName(readIndustryName());
      setPlan(res.data ?? null);
    } catch {
      setPlan(null);
    } finally {
      setLoading(false);
    }
  }, [clientId, roleId]);

  useEffect(() => {
    loadPlan();
  }, [loadPlan]);

  const featureRows = useMemo(
    () => buildPlanFeatureRows(plan?.features ?? plan?.Features, industryName),
    [plan, industryName],
  );

  if (isSuperAdminRole(roleId) || !clientId) return null;

  const planName = plan?.planName ?? plan?.PlanName;
  const hasActive = !!(plan?.hasActiveSubscription ?? plan?.HasActiveSubscription);
  const seatsUnlimited = !!(plan?.seatsUnlimited ?? plan?.SeatsUnlimited);
  const seatLimit = plan?.seatLimit ?? plan?.SeatLimit;
  const seatUsed = plan?.seatUsed ?? plan?.SeatUsed;
  const enabledCount = countEnabledFeatures(featureRows);

  return (
    <>
      <div className="nf-sidebar-plan-card">
        <div className="nf-sidebar-plan-card-head">
          <i className="fas fa-id-card" aria-hidden="true" />
          <span>Your plan</span>
        </div>
        {loading ? (
          <p className="nf-sidebar-plan-card-muted">
            <i className="fas fa-spinner fa-spin" aria-hidden="true" /> Loading…
          </p>
        ) : hasActive && planName ? (
          <>
            <p className="nf-sidebar-plan-card-name">{planName}</p>
            <p className="nf-sidebar-plan-card-meta">
              {enabledCount} modules included
              {seatsUnlimited ? ' · Unlimited seats' : seatLimit != null ? ` · ${seatUsed ?? 0}/${seatLimit} seats` : ''}
            </p>
          </>
        ) : (
          <p className="nf-sidebar-plan-card-muted">No active subscription</p>
        )}
        <div className="nf-sidebar-plan-card-actions">
          <button
            type="button"
            className="nf-sidebar-plan-card-btn"
            disabled={loading || !hasActive}
            onClick={() => setDrawerOpen(true)}
          >
            View features
          </button>
          <Link to="/settings?tab=plan" className="nf-sidebar-plan-card-link">
            Settings
          </Link>
        </div>
      </div>

      <AgencyUserDrawer
        open={drawerOpen}
        title="Your plan"
        subtitle={planName ? `${planName}${industryName ? ` · ${industryName}` : ''}` : industryName || 'Current plan'}
        onClose={() => setDrawerOpen(false)}
        footer={
          <button type="button" className="nf-btn nf-btn-primary nf-btn-sm" onClick={() => setDrawerOpen(false)}>
            Close
          </button>
        }
        width="min(520px, 100vw)"
      >
        <div className="nf-ws-drawer-form nf-ws-billing-features">
          <p className="nf-ws-billing-add-hint">
            Modules included in your subscription plan
            {industryName ? (
              <>
                {' '}
                for <strong>{industryName}</strong>
              </>
            ) : null}
            . Contact support to upgrade.
          </p>
          {!seatsUnlimited && seatLimit != null ? (
            <p className="nf-ws-billing-features-seats">
              <i className="fas fa-users" aria-hidden="true" /> Seats: {seatUsed ?? 0} used / {seatLimit}
            </p>
          ) : seatsUnlimited ? (
            <p className="nf-ws-billing-features-seats">
              <i className="fas fa-users" aria-hidden="true" /> Seats: Unlimited
            </p>
          ) : null}
          <PlanFeaturesList rows={featureRows} />
        </div>
      </AgencyUserDrawer>
    </>
  );
}
